import { Fragment, useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Combobox, Dialog, Transition } from '@headlessui/react';
import { useTenantQuery } from '@/hooks/useTenantQuery';
import { useIncidentStore } from '@/store/incident.store';
import { useCommandPalette } from '@/hooks/useCommandPalette';
import { useKeyboardShortcut, getShortcutString } from '@/hooks/useKeyboardShortcut';
import { useKeyboardShortcutsModal } from '@/hooks/useKeyboardShortcutsModal';
import { Incident } from '@/types/incident';
import { TaskWithDetails } from '@/types/task';
import FaIcon from './FaIcon';

// Command types
type CommandCategory = 'navigation' | 'actions' | 'incidents' | 'tasks' | 'recent';

interface Command {
  id: string;
  name: string;
  description?: string;
  category: CommandCategory;
  icon: JSX.Element;
  action: () => void;
  keywords?: string[];
}

// Category labels. Per the color budget, category icons are neutral —
// selection (Combobox active state) is the only accent in the palette.
const CATEGORY_CONFIG: Record<CommandCategory, { label: string }> = {
  recent: { label: 'Recent' },
  navigation: { label: 'Navigation' },
  actions: { label: 'Actions' },
  incidents: { label: 'Incidents' },
  tasks: { label: 'Tasks' },
};

export default function CommandPalette() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isOpen, close, toggle } = useCommandPalette();
  const { activeIncident } = useIncidentStore();
  const { open: openShortcutsModal } = useKeyboardShortcutsModal();

  const [query, setQuery] = useState('');
  const [recentPaths, setRecentPaths] = useState<string[]>([]);

  // Register keyboard shortcut (Cmd/Ctrl + K)
  useKeyboardShortcut({
    key: 'k',
    modifiers: { ctrl: true, meta: true },
    onTrigger: toggle,
  });

  // Track recent paths
  useEffect(() => {
    const MAX_RECENT = 5;
    const currentPath = location.pathname;

    setRecentPaths((prev) => {
      const filtered = prev.filter((p) => p !== currentPath);
      return [currentPath, ...filtered].slice(0, MAX_RECENT);
    });
  }, [location.pathname]);

  // Fetch incidents and tasks via React Query (cached, only fetches when palette is open)
  const { data: incidentsRes } = useTenantQuery<{ data: Incident[] }>(
    ['command-palette', 'incidents'],
    '/incidents',
    {
      params: { limit: 10, status: 'active,planned,stabilizing' },
      enabled: isOpen,
      staleTime: 60000,
    },
  );

  const { data: tasksRes } = useTenantQuery<{ data: TaskWithDetails[] }>(
    ['command-palette', 'tasks', activeIncident?.id],
    '/tasks',
    {
      params: { incident_id: activeIncident?.id, limit: 10 },
      enabled: isOpen && !!activeIncident,
      staleTime: 60000,
    },
  );

  const incidents = incidentsRes?.data || [];
  const tasks = (activeIncident ? tasksRes?.data : []) || [];

  // Build static commands
  const staticCommands: Command[] = useMemo(() => {
    const navCommands: Command[] = [
      {
        id: 'nav-dashboard',
        name: 'Dashboard',
        description: 'Go to dashboard',
        category: 'navigation',
        icon: <FaIcon icon="house" className="h-5 w-5" />,
        action: () => navigate('/'),
        keywords: ['home', 'dashboard', 'overview'],
      },
      {
        id: 'nav-map',
        name: 'Map',
        description: 'View unified map',
        category: 'navigation',
        icon: <FaIcon icon="location-dot" className="h-5 w-5" />,
        action: () => navigate('/map'),
        keywords: ['map', 'location', 'geospatial'],
      },
      {
        id: 'nav-incidents',
        name: 'Incidents',
        description: 'View all incidents',
        category: 'navigation',
        icon: <FaIcon icon="fire" className="h-5 w-5" />,
        action: () => navigate('/incidents'),
        keywords: ['incidents', 'emergencies', 'events'],
      },
      {
        id: 'nav-tasks',
        name: 'Tasks',
        description: 'View all tasks',
        category: 'navigation',
        icon: <FaIcon icon="circle-check" className="h-5 w-5" />,
        action: () => navigate('/tasks'),
        keywords: ['tasks', 'assignments', 'work'],
      },
      {
        id: 'nav-forms',
        name: 'Forms',
        description: 'View forms',
        category: 'navigation',
        icon: <FaIcon icon="file-lines" className="h-5 w-5" />,
        action: () => navigate('/forms'),
        keywords: ['forms', 'ics', 'documents'],
      },
      {
        id: 'nav-team',
        name: 'Team',
        description: 'View team members',
        category: 'navigation',
        icon: <FaIcon icon="users" className="h-5 w-5" />,
        action: () => navigate('/team'),
        keywords: ['team', 'users', 'members', 'people'],
      },
      {
        id: 'nav-shelters',
        name: 'Shelters',
        description: 'View shelters',
        category: 'navigation',
        icon: <FaIcon icon="building" className="h-5 w-5" />,
        action: () => navigate('/shelters'),
        keywords: ['shelters', 'housing', 'evacuation'],
      },
      {
        id: 'nav-audit',
        name: 'Audit Logs',
        description: 'View audit logs',
        category: 'navigation',
        icon: <FaIcon icon="clock" className="h-5 w-5" />,
        action: () => navigate('/audit-logs'),
        keywords: ['audit', 'logs', 'history', 'timeline'],
      },
      {
        id: 'nav-settings',
        name: 'Settings',
        description: 'Open settings',
        category: 'navigation',
        icon: <FaIcon icon="gear" className="h-5 w-5" />,
        action: () => navigate('/settings'),
        keywords: ['settings', 'preferences', 'configuration'],
      },
    ];

    const actionCommands: Command[] = [
      {
        id: 'action-keyboard-shortcuts',
        name: 'Keyboard Shortcuts',
        description: 'View all keyboard shortcuts',
        category: 'actions',
        icon: <FaIcon icon="circle-question" className="h-5 w-5" />,
        action: () => {
          close();
          openShortcutsModal();
        },
        keywords: ['keyboard', 'shortcuts', 'hotkeys', 'help', '?'],
      },
      {
        id: 'action-new-incident',
        name: 'New Incident',
        description: 'Create a new incident',
        category: 'actions',
        icon: <FaIcon icon="circle-plus" className="h-5 w-5" />,
        action: () => navigate('/incidents/new'),
        keywords: ['create', 'new', 'incident', 'emergency'],
      },
    ];

    // Only show task creation if incident is active
    if (activeIncident) {
      actionCommands.push({
        id: 'action-new-task',
        name: 'New Task',
        description: 'Create a new task',
        category: 'actions',
        icon: <FaIcon icon="circle-plus" className="h-5 w-5" />,
        action: () => navigate('/tasks?new=true'),
        keywords: ['create', 'new', 'task', 'assignment'],
      });
    }

    return [...navCommands, ...actionCommands];
  }, [navigate, activeIncident]);

  // Build recent commands from paths
  const recentCommands: Command[] = useMemo(() => {
    return recentPaths.slice(1).map((path, index) => {
      const pathName = path.split('/').filter(Boolean).join(' > ') || 'Dashboard';
      return {
        id: `recent-${index}`,
        name: pathName,
        description: 'Recently visited',
        category: 'recent' as CommandCategory,
        icon: <FaIcon icon="clock" className="h-5 w-5" />,
        action: () => navigate(path),
        keywords: [pathName],
      };
    });
  }, [recentPaths, navigate]);

  // Build incident commands
  const incidentCommands: Command[] = useMemo(() => {
    return incidents.map((incident) => ({
      id: `incident-${incident.id}`,
      name: incident.name,
      description: `${incident.incident_number} • ${incident.type}`,
      category: 'incidents' as CommandCategory,
      icon: <FaIcon icon="fire" className="h-5 w-5" />,
      action: () => navigate(`/incidents/${incident.id}`),
      keywords: [incident.name, incident.incident_number, incident.type],
    }));
  }, [incidents, navigate]);

  // Build task commands
  const taskCommands: Command[] = useMemo(() => {
    return tasks.map((task) => ({
      id: `task-${task.id}`,
      name: task.title,
      description: `${task.task_number} • ${task.status}`,
      category: 'tasks' as CommandCategory,
      icon: <FaIcon icon="circle-check" className="h-5 w-5" />,
      action: () => navigate(`/tasks/${task.id}`),
      keywords: [task.title, task.task_number, task.status],
    }));
  }, [tasks, navigate]);

  // Combine all commands
  const allCommands = useMemo(() => {
    return [...recentCommands, ...staticCommands, ...incidentCommands, ...taskCommands];
  }, [recentCommands, staticCommands, incidentCommands, taskCommands]);

  // Filter commands based on query
  const filteredCommands = useMemo(() => {
    if (!query) return allCommands;

    const lowerQuery = query.toLowerCase();
    return allCommands.filter((command) => {
      const nameMatch = command.name.toLowerCase().includes(lowerQuery);
      const descMatch = command.description?.toLowerCase().includes(lowerQuery);
      const keywordMatch = command.keywords?.some((k) => k.toLowerCase().includes(lowerQuery));
      return nameMatch || descMatch || keywordMatch;
    });
  }, [query, allCommands]);

  // Group commands by category
  const groupedCommands = useMemo(() => {
    const groups: Partial<Record<CommandCategory, Command[]>> = {};

    filteredCommands.forEach((command) => {
      if (!groups[command.category]) {
        groups[command.category] = [];
      }
      groups[command.category]!.push(command);
    });

    return groups;
  }, [filteredCommands]);

  // Handle command selection
  const handleSelect = (command: Command | null) => {
    if (command) {
      command.action();
      close();
      setQuery('');
    }
  };

  // Close and reset on Escape
  const handleClose = () => {
    close();
    setQuery('');
  };

  return (
    <Transition.Root show={isOpen} as={Fragment} afterLeave={() => setQuery('')}>
      <Dialog as="div" className="relative z-[9999]" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-[10000] overflow-y-auto p-4 sm:p-6 md:p-20">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="mx-auto max-w-2xl transform overflow-hidden rounded-xl bg-surface-primary shadow-2xl ring-1 ring-black/5 dark:ring-white/10 transition-all">
              <Combobox onChange={handleSelect}>
                {/* Search Input */}
                <div className="relative">
                  <div className="absolute left-4 top-3.5 text-theme-tertiary pointer-events-none">
                    <FaIcon icon="magnifying-glass" className="h-5 w-5" />
                  </div>
                  <Combobox.Input
                    className="h-14 w-full border-0 bg-transparent pl-12 pr-4 text-theme-primary placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-primary-500/50 text-base"
                    placeholder="Search commands, incidents, tasks..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    autoFocus
                  />
                  <div className="absolute right-4 top-4 text-xs text-theme-tertiary pointer-events-none">
                    {getShortcutString('k', { ctrl: true, meta: true })}
                  </div>
                </div>

                {/* Results */}
                {filteredCommands.length > 0 ? (
                  <Combobox.Options
                    static
                    className="max-h-[60vh] overflow-y-auto scroll-py-2 border-t border-theme-primary divide-y divide-gray-100 dark:divide-gray-700"
                  >
                    {Object.entries(groupedCommands).map(([category, commands]) => (
                      <div key={category} className="p-2">
                        <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-theme-tertiary">
                          {CATEGORY_CONFIG[category as CommandCategory].label}
                        </div>
                        {commands!.map((command) => (
                          <Combobox.Option
                            key={command.id}
                            value={command}
                            className={({ active }) =>
                              `flex cursor-pointer select-none items-center gap-3 rounded-lg px-3 py-2.5 transition-colors ${
                                active
                                  ? 'bg-primary-500 text-white'
                                  : 'text-theme-primary'
                              }`
                            }
                          >
                            {({ active }) => (
                              <>
                                <div
                                  className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md ${
                                    active
                                      ? 'text-white'
                                      : 'bg-surface-tertiary text-theme-secondary'
                                  }`}
                                >
                                  {command.icon}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium truncate">{command.name}</div>
                                  {command.description && (
                                    <div
                                      className={`text-sm truncate ${
                                        active
                                          ? 'text-white/80'
                                          : 'text-theme-tertiary'
                                      }`}
                                    >
                                      {command.description}
                                    </div>
                                  )}
                                </div>
                                <div
                                  className={`flex-shrink-0 ${
                                    active ? 'text-white/60' : 'text-theme-tertiary'
                                  }`}
                                >
                                  <FaIcon icon="chevron-right" className="h-4 w-4" />
                                </div>
                              </>
                            )}
                          </Combobox.Option>
                        ))}
                      </div>
                    ))}
                  </Combobox.Options>
                ) : query ? (
                  <div className="px-6 py-14 text-center">
                    <p className="text-sm text-theme-tertiary">
                      No results found for "{query}"
                    </p>
                  </div>
                ) : (
                  <div className="px-6 py-14 text-center">
                    <p className="text-sm text-theme-tertiary">
                      Type to search commands, incidents, and tasks
                    </p>
                  </div>
                )}

                {/* Footer hint */}
                <div className="flex items-center justify-between border-t border-theme-primary px-4 py-3 text-xs text-theme-tertiary">
                  <div className="flex items-center gap-4">
                    <span>
                      <kbd className="px-2 py-1 bg-surface-tertiary rounded">↑</kbd>{' '}
                      <kbd className="px-2 py-1 bg-surface-tertiary rounded">↓</kbd> to
                      navigate
                    </span>
                    <span>
                      <kbd className="px-2 py-1 bg-surface-tertiary rounded">↵</kbd> to
                      select
                    </span>
                  </div>
                  <span>
                    <kbd className="px-2 py-1 bg-surface-tertiary rounded">esc</kbd> to
                    close
                  </span>
                </div>
              </Combobox>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
